import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { FiCode, FiBook, FiAward, FiUsers, FiTrendingUp, FiStar, FiGlobe, FiBriefcase, FiBox, FiDatabase, FiCloud, FiZap, FiServer } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../features/theme/contexts/ThemeContext';
import HelpButton from '../../components/HelpButton';
import TechnologyCarousel, { Technology } from './components/TechnologyCarousel';
import CyberButton from './components/button';
import '@/styles/cyberpunk.css';
import '@/styles/tech-fonts.css';

// Matrix rain effect component
const MatrixRain = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const latin = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const nums = '0123456789';
    const special = '!@#$%^&*()_+-=[]{}|;\':",./<>?';
    const chars = latin.toLowerCase() + latin + nums + special;
    
    const fontSize = 12;
    const columns = Math.floor(canvas.width / fontSize);
    const drops: number[] = Array(columns).fill(0);
    
    let intervalId: number;
    
    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = '#0f0';
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        const text = chars.charAt(Math.floor(Math.random() * chars.length));
        
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        
        drops[i]++;
      }
    };
    
    intervalId = window.setInterval(draw, 120);
    
    return () => {
      window.clearInterval(intervalId);
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="fixed top-0 left-0 w-full h-full pointer-events-none opacity-10 z-0"
    />
  );
};

// Animated code block component
const AnimatedCodeBlock = () => {
  const [text, setText] = useState('');
  const fullText = `function initCyberpunk() {
  const skills = ['HTML', 'CSS', 'JavaScript'];
  const cyberpunk = {
    learn: () => console.log('Learning...'),
    code: () => console.log('Coding...'),
    hack: () => console.log('Hacking the system...')
  };
  
  return { skills, ...cyberpunk };
}

// Start your learning journey
const journey = initCyberpunk();
journey.learn();
`;

  useEffect(() => {
    let currentIndex = 0;
    
    const typingInterval = setInterval(() => {
      if (currentIndex < fullText.length) {
        setText(fullText.substring(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        
        // Reset after a delay
        setTimeout(() => {
          setText('');
          currentIndex = 0;
          
          // Start typing again
          setTimeout(() => {
            const newTypingInterval = setInterval(() => {
              if (currentIndex < fullText.length) {
                setText(fullText.substring(0, currentIndex + 1));
                currentIndex++;
              } else {
                clearInterval(newTypingInterval);
              }
            }, 30);
          }, 1000);
        }, 5000);
      }
    }, 30);
    
    return () => clearInterval(typingInterval);
  }, []);
  
  return (
    <motion.div 
      className="mockup-code bg-neutral text-primary border border-primary/30 shadow-xl w-full max-w-md lg:max-w-lg mx-auto"
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.8 }}
    >
      <pre className="px-4 py-3 text-green-400"><code>{text}</code></pre>
      <span className="code-cursor inline-block w-2 h-4 bg-primary ml-1 animate-pulse"></span>
    </motion.div>
  );
};

export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toggleTheme } = useTheme();
  const { scrollY } = useScroll();
  const mainRef = useRef(null);
  
  const headerOpacity = useTransform(scrollY, [0, 200], [1, 0.2]);
  const headerScale = useTransform(scrollY, [0, 200], [1, 0.8]);
  
  const technologies: Technology[] = [
    {
      id: 'web',
      icon: <FiGlobe />,
      title: t('home.technologies.web.title'),
      description: t('home.technologies.web.description')
    },
    {
      id: 'frontend',
      icon: <FiCode />,
      title: t('home.technologies.frontend.title'),
      description: t('home.technologies.frontend.description')
    },
    {
      id: 'backend',
      icon: <FiDatabase />,
      title: t('home.technologies.backend.title'),
      description: t('home.technologies.backend.description')
    },
    {
      id: 'cloud',
      icon: <FiCloud />,
      title: t('home.technologies.cloud.title'),
      description: t('home.technologies.cloud.description')
    },
    {
      id: 'career',
      icon: <FiBriefcase />,
      title: t('home.technologies.career.title'),
      description: t('home.technologies.career.description')
    }
  ];

  return (
    <motion.div
      ref={mainRef}
      className="min-h-screen bg-base-300 home-page overflow-x-hidden overflow-y-auto relative"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Matrix background effect */}
      <MatrixRain />
      
      {/* Hero Section */}
      <section className="hero-section min-h-[80vh] flex items-center justify-center p-3 bg-gradient-to-b from-black/80 via-base-300 to-base-100 relative">
        {/* Animated circuit lines */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="circuit-line w-full h-1 bg-primary/30 absolute top-[20%] left-0 animate-pulse"></div>
          <div className="circuit-line w-1 h-full bg-primary/30 absolute top-0 left-[30%] animate-pulse" style={{animationDelay: '0.5s'}}></div>
          <div className="circuit-line w-full h-1 bg-secondary/30 absolute top-[70%] left-0 animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="circuit-line w-1 h-full bg-secondary/30 absolute top-0 left-[80%] animate-pulse" style={{animationDelay: '1.5s'}}></div>
        </div>
        
        <div className="max-w-6xl w-full mx-auto grid md:grid-cols-2 gap-8 items-center">
          <div className="text-center md:text-left flex flex-col items-center md:items-start justify-center gap-6 z-10">
            <motion.div 
              style={{ opacity: headerOpacity, scale: headerScale }}
              className="glitch-container inline-block"
            >
              <h1 
                data-text="PROGRAME.TECH"
                className="text-3xl sm:text-5xl md:text-7xl font-bold text-primary font-tech tracking-wider whitespace-nowrap inline-block"
              >
                PROGRAME.TECH
              </h1>
            </motion.div>
            
            <motion.div 
              className="flex items-center gap-2 mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="badge badge-primary badge-outline py-3 px-4 text-xs font-mono flex items-center gap-2 neon-glow">
                <FiZap className="text-lg" /> 
                <span className="font-bold">CYBER CODING</span>
              </div>
              <div className="badge badge-secondary badge-outline py-3 px-4 text-xs font-mono flex items-center gap-2 neon-glow">
                <FiServer className="text-lg" /> 
                <span className="font-bold">TECH.FUTURE</span>
              </div>
            </motion.div>
            
            <motion.p 
              className="text-sm sm:text-xl md:text-2xl text-base-content/90 max-w-xl px-2 font-tech-secondary"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {t('home.hero.tagline')}
            </motion.p>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-4"
            >
              <CyberButton onClick={() => navigate('/register')}>
                {t('home.hero.cta')} <FiCode className="inline ml-2" />
              </CyberButton>
            </motion.div>
          </div>
          
          <div className="hidden md:flex justify-center items-center">
            <AnimatedCodeBlock />
          </div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center"
          animate={{ 
            y: [0, 10, 0],
            opacity: [0.4, 1, 0.4]
          }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-primary rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-primary rounded-full"></div>
          </div>
          <p className="text-xs mt-2 text-primary/70 font-tech-secondary">SCROLL</p>
        </motion.div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section pt-16 sm:pt-24 px-3 bg-base-100 relative">
        <div className="absolute inset-0 bg-[url('/images/grid-pattern.svg')] opacity-5 pointer-events-none"></div>
        
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="glitch-container text-center mb-14"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <h2 
              data-text={t('home.benefits.title')}
              className="text-3xl md:text-5xl font-bold text-primary font-tech-secondary tracking-wide inline-block"
            >
              {t('home.benefits.title')}
            </h2>
            <div className="h-1 w-20 bg-primary mt-4 mx-auto"></div>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div 
              className="card bg-base-200 shadow-xl border border-primary/20 backdrop-blur-sm overflow-hidden"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: 0.1, duration: 0.5 }}
              whileHover={{ 
                y: -10,
                boxShadow: "0 0 30px rgba(255, 0, 128, 0.3)"
              }}
            >
              <div className="absolute -top-10 -left-10 w-20 h-20 bg-primary/10 rounded-full blur-2xl"></div>
              <div className="card-body relative z-10">
                <FiTrendingUp className="text-5xl text-primary mb-5" />
                <h3 className="text-xl font-bold mb-3 text-base-content font-tech-secondary">{t('home.benefits.career.title')}</h3>
                <p className="text-base-content/80 font-tech-secondary">{t('home.benefits.career.description')}</p>
                <div className="absolute right-3 bottom-3 text-primary/10 text-7xl font-tech font-bold">01</div>
              </div>
            </motion.div>
            
            <motion.div 
              className="card bg-base-200 shadow-xl border border-primary/20 backdrop-blur-sm overflow-hidden"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: 0.2, duration: 0.5 }}
              whileHover={{ 
                y: -10,
                boxShadow: "0 0 30px rgba(0, 255, 255, 0.3)"
              }}
            >
              <div className="absolute -top-10 -right-10 w-20 h-20 bg-secondary/10 rounded-full blur-2xl"></div>
              <div className="card-body relative z-10">
                <FiStar className="text-5xl text-secondary mb-5" />
                <h3 className="text-xl font-bold mb-3 text-base-content font-tech-secondary">{t('home.benefits.practical.title')}</h3>
                <p className="text-base-content/80 font-tech-secondary">{t('home.benefits.practical.description')}</p>
                <div className="absolute right-3 bottom-3 text-secondary/10 text-7xl font-tech font-bold">02</div>
              </div>
            </motion.div>
            
            <motion.div 
              className="card bg-base-200 shadow-xl border border-primary/20 backdrop-blur-sm overflow-hidden"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: 0.3, duration: 0.5 }}
              whileHover={{ 
                y: -10,
                boxShadow: "0 0 30px rgba(255, 208, 0, 0.3)"
              }}
            >
              <div className="absolute -bottom-10 -right-10 w-20 h-20 bg-accent/10 rounded-full blur-2xl"></div>
              <div className="card-body relative z-10">
                <FiUsers className="text-5xl text-accent mb-5" />
                <h3 className="text-xl font-bold mb-3 text-base-content font-tech-secondary">{t('home.benefits.community.title')}</h3>
                <p className="text-base-content/80 font-tech-secondary">{t('home.benefits.community.description')}</p>
                <div className="absolute right-3 bottom-3 text-accent/10 text-7xl font-tech font-bold">03</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section py-24 px-4 bg-base-300 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none"></div>
        
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="glitch-container text-center mb-14"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <h2 
              data-text={t('home.features.title')}
              className="text-3xl md:text-5xl font-bold text-primary font-tech-secondary tracking-wide inline-block"
            >
              {t('home.features.title')}
            </h2>
            <div className="h-1 w-20 bg-primary mt-4 mx-auto"></div>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              className="card bg-base-100 shadow-xl border border-primary/20 backdrop-blur-sm"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: 0.1, duration: 0.5 }}
              whileHover={{ 
                y: -10,
                boxShadow: "0 0 20px rgba(255, 0, 128, 0.3)"
              }}
            >
              <div className="card-body">
                <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <FiBook className="text-4xl text-primary" />
                </div>
                <h3 className="card-title text-base-content font-tech-secondary">{t('home.features.courses.title')}</h3>
                <p className="text-base-content/80 font-tech-secondary">{t('home.features.courses.description')}</p>
                <div className="card-actions justify-end mt-3">
                  <div className="badge badge-outline badge-primary">Learning</div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="card bg-base-100 shadow-xl border border-primary/20 backdrop-blur-sm"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: 0.2, duration: 0.5 }}
              whileHover={{ 
                y: -10,
                boxShadow: "0 0 20px rgba(0, 255, 255, 0.3)"
              }}
            >
              <div className="card-body">
                <div className="w-14 h-14 rounded-lg bg-secondary/10 flex items-center justify-center mb-4">
                  <FiCode className="text-4xl text-secondary" />
                </div>
                <h3 className="card-title text-base-content font-tech-secondary">{t('home.features.challenges.title')}</h3>
                <p className="text-base-content/80 font-tech-secondary">{t('home.features.challenges.description')}</p>
                <div className="card-actions justify-end mt-3">
                  <div className="badge badge-outline badge-secondary">Coding</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="card bg-base-100 shadow-xl border border-primary/20 backdrop-blur-sm"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: 0.3, duration: 0.5 }}
              whileHover={{ 
                y: -10,
                boxShadow: "0 0 20px rgba(255, 208, 0, 0.3)"
              }}
            >
              <div className="card-body">
                <div className="w-14 h-14 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                  <FiAward className="text-4xl text-accent" />
                </div>
                <h3 className="card-title text-base-content font-tech-secondary">{t('home.features.elo.title')}</h3>
                <p className="text-base-content/80 font-tech-secondary">{t('home.features.elo.description')}</p>
                <div className="card-actions justify-end mt-3">
                  <div className="badge badge-outline badge-accent">Ranking</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="card bg-base-100 shadow-xl border border-primary/20 backdrop-blur-sm"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: 0.4, duration: 0.5 }}
              whileHover={{ 
                y: -10,
                boxShadow: "0 0 20px rgba(255, 0, 255, 0.3)"
              }}
            >
              <div className="card-body">
                <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <FiBriefcase className="text-4xl text-primary" />
                </div>
                <h3 className="card-title text-base-content font-tech-secondary">{t('home.features.career.title')}</h3>
                <p className="text-base-content/80 font-tech-secondary">{t('home.features.career.description')}</p>
                <div className="card-actions justify-end mt-3">
                  <div className="badge badge-outline badge-primary">Future</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="technologies-section py-24 px-4 bg-base-100 relative">
        <div className="absolute inset-0 scanlines opacity-10 pointer-events-none"></div>
        
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="glitch-container text-center mb-14"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <h2 
              data-text={t('home.technologies.title')}
              className="text-3xl md:text-5xl font-bold text-primary font-tech-secondary tracking-wide inline-block"
            >
              {t('home.technologies.title')}
            </h2>
            <div className="h-1 w-20 bg-primary mt-4 mx-auto"></div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
          >
            <TechnologyCarousel technologies={technologies} />
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-24 px-4 bg-base-300 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial from-primary/20 via-transparent to-transparent opacity-50 pointer-events-none"></div>
        
        {/* Animated circuit lines */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="circuit-line w-full h-1 bg-primary/20 absolute top-[10%] left-0 animate-pulse"></div>
          <div className="circuit-line w-1 h-full bg-secondary/20 absolute top-0 left-[20%] animate-pulse" style={{animationDelay: '0.7s'}}></div>
          <div className="circuit-line w-full h-1 bg-accent/20 absolute top-[90%] left-0 animate-pulse" style={{animationDelay: '1.2s'}}></div>
          <div className="circuit-line w-1 h-full bg-primary/20 absolute top-0 left-[80%] animate-pulse" style={{animationDelay: '1.7s'}}></div>
        </div>
        
        <motion.div 
          className="max-w-3xl mx-auto text-center relative z-10"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
        >
          <div className="glitch-container">
            <h2 
              data-text={t('home.cta.title')}
              className="text-3xl md:text-5xl font-bold mb-6 text-primary font-tech tracking-wide"
            >
              {t('home.cta.title')}
            </h2>
          </div>
          
          <p className="text-xl mb-10 text-base-content/90 font-tech-secondary max-w-xl mx-auto">
            {t('home.cta.description')}
          </p>
          
          <motion.div 
            className="flex justify-center items-center mt-8"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <CyberButton onClick={() => navigate('/register')}>
              {t('home.cta.button')} <FiCode className="inline ml-2" />
            </CyberButton>
          </motion.div>
        </motion.div>
      </section>

      {/* Discord Community Section */}
      <section className="py-20 px-3 bg-base-100 relative">
        <div className="absolute inset-0 bg-[url('/images/circuit-pattern.svg')] opacity-5 pointer-events-none"></div>
        
        <motion.div 
          className="max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="glitch-container mb-4">
            <h2 
              data-text="AINDA COM DÚVIDAS?"
              className="text-2xl sm:text-3xl md:text-5xl font-bold text-primary font-tech tracking-wide"
            >
              AINDA COM DÚVIDAS?
            </h2>
          </div>
          
          <p className="text-base sm:text-xl mb-8 text-base-content/90 px-2 font-tech-secondary">
            Entre para nossa comunidade no Discord e tire todas as suas dúvidas com outros desenvolvedores!
          </p>
          
          <motion.div 
            className="flex justify-center items-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <CyberButton onClick={() => navigate('/community')} className="min-w-[200px]">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                <FiUsers className="text-xl" />
                <span className="text-center">Entrar no Discord</span>
              </div>
            </CyberButton>
          </motion.div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="footer footer-center p-10 bg-base-300 text-base-content border-t border-primary/10">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="glitch-container">
            <p 
              data-text="PROGRAME.TECH"
              className="font-bold text-primary font-tech tracking-wide text-xl"
            >
              PROGRAME.TECH
            </p>
          </div>
          <p className="text-sm mt-2 font-tech-secondary text-base-content/70">
            © 2024 • Aprenda. Code. Evolua.
          </p>
        </motion.div>
      </footer>
    </motion.div>
  );
}